// ============================================================================
// MyProfilePage — MON profil (L16, F-800..F-809) — /profile
// ============================================================================
// Portage de (dashboard)/profile/page.tsx au standard nav maison (gabarit E1b/E1c) :
// ProfileSummaryHeader -> ProfileTabNav -> contenu de l'onglet.
//
// F-800 : aucune garde de role au-dela du standing GEST — c'est SA fiche.
// F-801 : onglets FIXES ; « gestion » n'y figure JAMAIS (M-803 : c'est la vue RH
//         d'un employe par un tiers, elle n'a pas de sens sur soi).
// F-802 : deep-link `?tab=` via resolveProfileTab ; invalide -> apercu (M-802).
// F-808 : pouls/objectifs/documents/remuneration accessibles SANS aucune garde.
// M-803 : matricule + date d'arrivee toujours affiches sur soi.
// M-807 : MetaForm et ProfileForm n'ont AUCUN ciblage — ils editent SA fiche.
//
// F-803 (AvatarForm) : NON monte — l'upload d'avatar est DIFFERE (bucket Firebase
// profil non migre, cf. GEST-STORAGE-MIGRATION). Aucun formulaire desactive
// « fantome » : on ne montre pas une action qu'on ne peut pas rendre.
// F-806 (NotificationPrefsSection) : hors scope (module notifs).
//
// Onglets pouls / objectifs / documents : la GARDE est portee et prouvee, mais
// leurs composants de contenu (MemberPulseHistory / ObjectivesBoard /
// EmployeeDocumentsSection) sont HORS du manifeste L16 -> EmptyState « a venir »
// honnete (meme traitement que ManagementTab). Les documents dependent en plus du
// bucket Firebase non migre.

import { useSearchParams } from 'react-router-dom';
import { EmptyState, QueryError, SkeletonCard, SkeletonLine } from '@gs/ui';
import { Activity, ClipboardList, FileText, UserRound } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useMyProfile } from '../../hooks/useProfile';
import { ProfileTabNav } from '../../components/profile/ProfileTabNav';
import { resolveProfileTab } from '../../components/profile/ProfileTabNav.utils';
import { ProfileSummaryHeader } from '../../components/profile/ProfileSummaryHeader';
import { OverviewStats } from '../../components/profile/OverviewStats';
import { StrengthsOverview } from '../../components/profile/StrengthsOverview';
import { CompetencyCardsSection } from '../../components/profile/CompetencyCardsSection';
import { MetaForm } from '../../components/profile/MetaForm';
import { ProfileForm } from '../../components/profile/ProfileForm';
import { CompensationSection } from '../../components/profile/CompensationSection';
import { derivePersonRole } from '../../lib/org/derivePersonRole';
import { ProfileForbidden, ComingSoonTab, Collapsible } from './shared';

function ProfileSkeleton() {
  return (
    <div className="space-y-4">
      <SkeletonLine className="h-4 w-48" />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
}

export default function MyProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const q = useMyProfile(!!user);
  const forbidden = q.isError && q.error instanceof ApiForbiddenError;

  // F-802 : la liste des onglets vient du SERVEUR (jamais recalculee ici).
  const tabs = q.data?.tabs ?? ['apercu'];
  const tab = resolveProfileTab(searchParams.get('tab'), tabs);

  if (q.isLoading) return <ProfileSkeleton />;
  if (forbidden) return <ProfileForbidden />;
  if (q.isError) return <QueryError message={t('gest:profile.loadError')} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;

  const { profile, overview, compensation } = q.data;

  // Donnees non migrees (gest.EmployeeProfiles vide en prod — item 14 roadmap) :
  // etat GERE nativement, pas un bug. Meme parti pris qu'E1a.
  if (!profile) {
    return (
      <EmptyState
        icon={<UserRound size={48} aria-hidden="true" />}
        title={t('gest:profile.noRecord.title')}
        description={t('gest:profile.noRecord.description')}
      />
    );
  }

  return (
    <div className="space-y-5">
      <ProfileSummaryHeader
        eyebrow={t('gest:profile.eyebrow.self')}
        fullName={profile.fullName}
        email={profile.email}
        role={derivePersonRole({
          systemCRole: profile.systemCRole,
          isTeamDirector: profile.isTeamDirector === true,
        }) ?? 'employee'}
        committee={profile.committee}
        jobTitle={profile.jobTitle}
        teamName={profile.teamName}
        // M-803 : sur SOI, toujours affiches (aucune garde).
        matricule={profile.matricule}
        hireDate={compensation?.hireDate ?? null}
        responsable={profile.responsable}
      >
        <div className="flex flex-col gap-3">
          {profile.tasksDescription ? (
            <p className="whitespace-pre-wrap text-field text-slate-600 dark:text-slate-300">
              {profile.tasksDescription}
            </p>
          ) : null}
          {/* F-804 : replie par defaut. */}
          <Collapsible summary={t('gest:profile.meta.collapsible')}>
            <MetaForm
              initialJobTitle={profile.jobTitle}
              initialTasksDescription={profile.tasksDescription}
            />
          </Collapsible>
        </div>
      </ProfileSummaryHeader>

      <ProfileTabNav basePath="/profile" active={tab} available={tabs} />

      {tab === 'apercu' ? (
        <div className="flex flex-col gap-6">
          <OverviewStats
            memberPulses={overview?.pulses ?? []}
            objectives={overview?.objectives ?? []}
            hireDate={compensation?.hireDate ?? null}
          />
          {/* Sur SON profil, forces/Nova toujours visibles + editables (F-833). */}
          <StrengthsOverview
            strengths={profile.gallupStrengths}
            nova={profile.nova}
            editAction={
              // F-805 : replie par defaut.
              <Collapsible summary={t('gest:profile.form.collapsible')}>
                <ProfileForm initialStrengths={profile.gallupStrengths} initialNova={profile.nova} />
              </Collapsible>
            }
          />
          {/* Mig 264 : SES cartes de competence, numero compris, lecture seule. */}
          <CompetencyCardsSection />
        </div>
      ) : null}

      {tab === 'pouls' ? <ComingSoonTab icon={<Activity size={48} aria-hidden="true" />} lot="pulse" /> : null}
      {tab === 'objectifs' ? <ComingSoonTab icon={<ClipboardList size={48} aria-hidden="true" />} lot="objectives" /> : null}
      {tab === 'documents' ? <ComingSoonTab icon={<FileText size={48} aria-hidden="true" />} lot="documents" /> : null}
      {/* F-808 : la remuneration de SOI est accessible sans aucune garde de role. */}
      {tab === 'remuneration' ? <CompensationSection compensation={compensation} /> : null}
    </div>
  );
}
